import React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface RetirementPlanningFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
  participants?: Array<{ id: string; vorname: string; nachname: string }>;
}

// Digital access options
const DIGITAL_ACCESS_OPTIONS = [
  { id: "finanzonline", label: "Finanzonline" },
  { id: "id_austria", label: "ID Austria" },
];

// Provision types for the table
const PROVISION_TYPES = [
  { id: "pzv", label: "PZV" },
  { id: "klassische_lv_pv", label: "Klassische\nLV/PV" },
  { id: "flv", label: "FLV" },
  { id: "bav", label: "BAV" },
  { id: "sonstiges", label: "Sonstiges" },
];

// Situation rows
const SITUATION_ROWS = [
  { id: "wunschpension", label: "Wunschpension" },
  { id: "alterspension_ca", label: "Alterspension ca." },
  { id: "luecke_zur_wunschpension", label: "Lücke zur Wunschpension" },
];

export function RetirementPlanningForm({
  formValues,
  onFieldChange,
  className,
  participants = [],
}: RetirementPlanningFormProps) {
  // Use default participants if none provided
  const displayParticipants = participants.length > 0 
    ? participants 
    : [
        { id: "gp1", vorname: "Gesprächspartner", nachname: "1" },
        { id: "gp2", vorname: "Gesprächspartner", nachname: "2" },
      ];

  const digitalAccess: Record<string, Record<string, boolean>> = formValues.altersvorsorge_digital_access || {};
  const provisions: Record<string, Record<string, string>> = formValues.altersvorsorge_provisions || {};
  const situation: Record<string, Record<string, string>> = formValues.altersvorsorge_situation || {};
  const monthlyAmount: string = formValues.altersvorsorge_monthly || "";
  const taxBenefits: string = formValues.altersvorsorge_tax_benefits || "";
  const notes: string = formValues.altersvorsorge_notes || "";
  const noExisting: boolean = formValues.altersvorsorge_no_existing || false;
  const copyPhoto: boolean = formValues.altersvorsorge_copy_photo || false;

  const toggleDigitalAccess = (participantId: string, optionId: string) => {
    const participantAccess = digitalAccess[participantId] || {};
    const updated = {
      ...digitalAccess,
      [participantId]: {
        ...participantAccess,
        [optionId]: !participantAccess[optionId],
      },
    };
    onFieldChange("altersvorsorge_digital_access", updated);
  };

  const updateProvision = (row: string, col: string, value: string) => {
    const rowData = provisions[row] || {};
    const updated = {
      ...provisions,
      [row]: { ...rowData, [col]: value },
    };
    onFieldChange("altersvorsorge_provisions", updated);
  };

  const updateSituation = (row: string, participantId: string, value: string) => {
    const rowData = situation[row] || {};
    const updated = {
      ...situation,
      [row]: { ...rowData, [participantId]: value },
    };
    onFieldChange("altersvorsorge_situation", updated);
  };

  const getParticipantLabel = (participant: typeof displayParticipants[0], index: number) => {
    const name = `${participant.vorname || ''} ${participant.nachname || ''}`.trim();
    return name || `GP${index + 1}`;
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Lebensqualität </span>
          <span className="text-primary">im Alter</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Mehr Lebensqualität im Alter.
        </p>
      </div>

      {/* Section: Digital Access */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          Besteht einer der nachfolgenden Zugänge?
        </h3>
        <div className="grid grid-cols-2 gap-8">
          {displayParticipants.map((participant, pIdx) => (
            <div key={participant.id} className="space-y-2">
              <Label className="font-semibold text-sm">
                {getParticipantLabel(participant, pIdx)}
              </Label>
              <div className="space-y-2">
                {DIGITAL_ACCESS_OPTIONS.map((option) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`access-${participant.id}-${option.id}`}
                      checked={digitalAccess[participant.id]?.[option.id] || false}
                      onCheckedChange={() => toggleDigitalAccess(participant.id, option.id)}
                    />
                    <Label
                      htmlFor={`access-${participant.id}-${option.id}`}
                      className="text-sm cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section: Existing Provisions Table */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          Welche Vorsorge haben Sie bisher getroffen?
        </h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-3 font-semibold w-32"></th>
                {PROVISION_TYPES.map((type) => (
                  <th
                    key={type.id}
                    className="text-center p-3 font-semibold text-primary whitespace-pre-line text-xs"
                  >
                    {type.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayParticipants.map((participant, pIdx) => (
                <tr key={participant.id} className="border-t">
                  <td className="p-3 font-medium text-sm">
                    {getParticipantLabel(participant, pIdx)}
                  </td>
                  {PROVISION_TYPES.map((type) => (
                    <td key={type.id} className="p-2">
                      <Input
                        value={provisions[participant.id]?.[type.id] || ""}
                        onChange={(e) => updateProvision(participant.id, type.id, e.target.value)}
                        className="h-8 text-sm text-center"
                        placeholder=""
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t">
                <td className="p-3 font-medium text-sm">Gesellschaft</td>
                {PROVISION_TYPES.map((type) => (
                  <td key={type.id} className="p-2">
                    <Input
                      value={provisions["gesellschaft"]?.[type.id] || ""}
                      onChange={(e) => updateProvision("gesellschaft", type.id, e.target.value)}
                      className="h-8 text-sm text-center"
                      placeholder=""
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Checkboxes below table */}
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              id="no-existing"
              checked={noExisting}
              onCheckedChange={(checked) => onFieldChange("altersvorsorge_no_existing", checked)}
            />
            <Label htmlFor="no-existing" className="text-sm cursor-pointer">
              keine vorhanden
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="copy-photo"
              checked={copyPhoto}
              onCheckedChange={(checked) => onFieldChange("altersvorsorge_copy_photo", checked)}
            />
            <Label htmlFor="copy-photo" className="text-sm cursor-pointer">
              Kopie oder Foto
            </Label>
          </div>
        </div>
      </div>

      {/* Section: Current Situation Table */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          Ihre derzeitige Situation?
        </h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-3 font-semibold w-48"></th>
                {displayParticipants.map((participant, pIdx) => (
                  <th key={participant.id} className="text-center p-3 font-semibold text-primary">
                    {getParticipantLabel(participant, pIdx)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SITUATION_ROWS.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="p-3 font-medium text-sm">{row.label}</td>
                  {displayParticipants.map((participant) => (
                    <td key={participant.id} className="p-2">
                      <Input
                        value={situation[row.id]?.[participant.id] || ""}
                        onChange={(e) => updateSituation(row.id, participant.id, e.target.value)}
                        className="h-8 text-sm text-center"
                        placeholder=""
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section: Monthly Amount */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          Wie viel können Sie derzeit monatlich für Ihre Pension weglegen?
        </h3>
        <div className="flex items-center gap-2 max-w-xs">
          <span className="text-lg">€</span>
          <Input
            value={monthlyAmount}
            onChange={(e) => onFieldChange("altersvorsorge_monthly", e.target.value)}
            className="h-10"
            placeholder=""
          />
          <span className="text-sm text-muted-foreground whitespace-nowrap">monatlich</span>
        </div>
      </div>

      {/* Section: Tax Benefits */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground">
          Möchten Sie steuerliche Vorteile oder Förderungen nutzen?
        </h3>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              id="tax-yes"
              checked={taxBenefits === "ja"}
              onCheckedChange={() => onFieldChange("altersvorsorge_tax_benefits", taxBenefits === "ja" ? "" : "ja")}
            />
            <Label htmlFor="tax-yes" className="text-sm cursor-pointer">Ja</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="tax-no"
              checked={taxBenefits === "nein"}
              onCheckedChange={() => onFieldChange("altersvorsorge_tax_benefits", taxBenefits === "nein" ? "" : "nein")}
            />
            <Label htmlFor="tax-no" className="text-sm cursor-pointer">Nein</Label>
          </div>
        </div>
      </div>

      {/* Section: Notes */}
      <div className="space-y-3">
        <div className="border-2 border-muted rounded-lg p-4">
          <Label className="text-sm font-semibold text-foreground">Notizen:</Label>
          <Textarea
            value={notes}
            onChange={(e) => onFieldChange("altersvorsorge_notes", e.target.value)}
            placeholder="Ihre Notizen hier..."
            className="mt-2 min-h-[100px] resize-none"
          />
        </div>
      </div>
    </div>
  );
}
